import { useCallback } from 'react';
import { ApiError, bffUpload, legacy } from '@/shared/api/client';
import type { FabButton, FabButtonType } from '@/shared/types/fab-button';
import type { DataTableFetchParams, DataTableFetchResult } from '@/shared/components/data-table';
import {
  fabDataTableColumns,
  mapLegacyFab,
  mapToLegacyFabBody,
  type LegacyFab,
} from '../model/fab-adapter';
import { defaultFabIconSvg, prepareFabIconFile } from '../model/fab-svg';
import { mgmFabIconSvg } from '../model/mgm-fab';

/** API legado responde 404 quando o PATCH não altera campos (nModified === 0). */
function isNoChangesPatchError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 404) return false;
  const msg = err.message.toLowerCase();
  const dataMsg =
    err.data && typeof err.data === 'object' && 'error' in err.data
      ? String((err.data as { error?: unknown }).error ?? '').toLowerCase()
      : '';
  return msg.includes('no changes made') || dataMsg.includes('no changes made');
}

function buildBody(params: DataTableFetchParams) {
  const columns = fabDataTableColumns();
  const order =
    params.sorting.length > 0
      ? params.sorting.map((s) => ({
          column: columns.findIndex((c) => c.data === s.id),
          dir: s.desc ? ('desc' as const) : ('asc' as const),
        }))
      : [{ column: 5, dir: 'asc' as const }];

  return {
    length: params.pageSize,
    skip: params.pageIndex * params.pageSize,
    search: { value: params.search },
    order,
    columns,
  };
}

export function useFabButtonsFetcher(idPassbook: string) {
  return useCallback(
    async (params: DataTableFetchParams): Promise<DataTableFetchResult<FabButton>> => {
      const res = await legacy.post<{ data?: LegacyFab[]; filtered?: number }>(
        `/fabs/data/${idPassbook}`,
        buildBody(params),
        { headers: { 'X-Passbook-Id': idPassbook } },
      );
      const rows = (res.data ?? []).map(mapLegacyFab);
      return { rows, total: res.filtered ?? rows.length };
    },
    [idPassbook],
  );
}

export interface FabButtonPayload {
  action: 'insert' | 'edit';
  id?: string;
  idPassbook: string;
  label: string;
  type: FabButtonType;
  /** Tipo antes da edição (sair de "Indique e ganhe" limpa o kind no legado). */
  previousType?: FabButtonType;
  value: string;
  color?: string;
  iconColor?: string;
  /** Cor do ícone antes da edição — se mudou, reenvia SVG colorido. */
  previousIconColor?: string;
  /** SVG atual (texto) para recolorir sem novo arquivo. */
  existingSvgContent?: string | null;
  active: boolean;
  launchDirect: boolean;
  iconFile?: File | null;
}

export async function saveFabButton(payload: FabButtonPayload) {
  const body = mapToLegacyFabBody(payload);
  const headers = { headers: { 'X-Passbook-Id': payload.idPassbook } };

  if (payload.action === 'insert') {
    const iconColor = payload.iconColor ?? '#ffffff';
    const iconFile = await prepareFabIconFile(
      payload.iconFile ?? null,
      iconColor,
      payload.type === 'mgm' ? mgmFabIconSvg() : defaultFabIconSvg(),
    );
    const fd = new FormData();
    Object.entries(body).forEach(([key, value]) => {
      fd.append(key, String(value));
    });
    fd.append('icon', iconFile);
    await bffUpload(`/bff/uploads/fab/${payload.idPassbook}`, fd);
    return;
  }

  if (!payload.id) throw new Error('ID do botão é obrigatório para edição.');

  try {
    await legacy.patch(`/fabs/${payload.id}`, body, headers);
  } catch (err) {
    // Só SVG/cor mudou: metadados iguais → API retorna 404 "no changes made".
    // Segue para o upload do ícone em vez de abortar o save.
    if (!isNoChangesPatchError(err)) throw err;
  }

  const iconColor = payload.iconColor ?? '#ffffff';
  const colorChanged =
    payload.iconColor != null &&
    payload.previousIconColor != null &&
    payload.iconColor !== payload.previousIconColor;
  const hasSvgSource = Boolean(payload.iconFile) || Boolean(payload.existingSvgContent);

  if (payload.iconFile || (colorChanged && hasSvgSource)) {
    const iconFile = await prepareFabIconFile(
      payload.iconFile ?? null,
      iconColor,
      payload.existingSvgContent ?? defaultFabIconSvg(),
    );
    await updateFabIcon(payload.id, iconFile);
  }
}

export async function updateFabIcon(fabId: string, icon: File) {
  const formData = new FormData();
  formData.append('icon', icon);
  await bffUpload(`/bff/uploads/fab/${fabId}/image`, formData);
}

/** Remove permanentemente o botão flutuante. */
export async function deleteFabButton(idPassbook: string, id: string) {
  await legacy.delete(`/fabs/${id}?passbookId=${encodeURIComponent(idPassbook)}`, {
    headers: { 'X-Passbook-Id': idPassbook },
  });
}

/** @deprecated Preferir deleteFabButton — mantido só como alias de desativar. */
export async function removeFabButton(idPassbook: string, id: string) {
  await toggleFabButtonActive(idPassbook, id, false);
}

export async function toggleFabButtonActive(idPassbook: string, id: string, active: boolean) {
  await legacy.patch(`/fabs/${id}`, { active }, { headers: { 'X-Passbook-Id': idPassbook } });
}

export async function moveFabButtonOrder(idPassbook: string, id: string, direction: 'up' | 'down') {
  await legacy.patch(
    `/fabs/${id}/order`,
    { direction, passbookId: idPassbook },
    { headers: { 'X-Passbook-Id': idPassbook } },
  );
}

/** Reordena via drag-and-drop chamando moveFabButtonOrder em sequência. */
export async function reorderFabButton(idPassbook: string, id: string, from: number, to: number): Promise<void> {
  const delta = to - from;
  if (delta === 0) return;
  const direction = delta > 0 ? 'down' : 'up';
  for (let i = 0; i < Math.abs(delta); i++) {
    await moveFabButtonOrder(idPassbook, id, direction);
  }
}
